import React from "react";
import { Button, Menu } from "antd";
import { observer } from "mobx-react-lite";
import { CaretUpOutlined } from "@ant-design/icons";
import ShopType from "@/enum/ShopType";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import ShopDTO from "@/dto/shop/ShopDTO";
import OrderDTO from "@/dto/order/OrderDTO";
import { ShippingWay } from "@/services/api/orderShip";
import { useGlobalStore } from "@/store/hooks";
import { useModalStore } from "@/hooks/Modal/hooks";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { DisabledPendingShipmentRefundingShipShopTypes, SupplierShopTypes } from "@/utils/ShopUtils";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import { execJdSellerDelivery, execOfflineShip, execOnlineShip, execVirtualShip, execReshipOrder } from "./services";
import AutoLoadingDropdownButton from "@/components/AutoLoadingDropdownButton";
import useShip from "../../dialogs/ShipDialog/useShip";
import useReship from "../../dialogs/ReshipOrderDialog/useReship";
import { MenuDivider, MenuItem, MenuItemGroup, SubMenu, buildMenuItems } from "@/utils/antdItems";

interface ShipButtonProps {
    tab: OrderTab;
    shop: ShopDTO;
    getSelectedOrders(): OrderDTO[];
}

const ShipButton: React.FC<ShipButtonProps> = function (props) {
    const orderManagementStore = useOrderManagementStore();

    const { tab, shop, getSelectedOrders } = props;
    const allOrders = orderManagementStore.table.orders;
    const globalStore = useGlobalStore();
    const modalStore = useModalStore();

    const onShipTaskDone = async () => {
        orderManagementStore.fetchOrders(shop.id, tab);

        orderManagementStore.getOrderListCount();
    };

    const updateOrder = (orderId: number, params: Partial<OrderDTO>) => {
        orderManagementStore.table.updateOrder(orderId, params);
    };

    const { submitShip } = useShip(onShipTaskDone);
    const { submitReship } = useReship(onShipTaskDone);

    const handleOfflineShip = () => {
        return execOfflineShip({
            globalStore,
            modalStore,
            allOrders,
            selectedOrders: getSelectedOrders(),
            shipOrder: submitShip,
            updateOrder,
        });
    };

    const handleReshipOrder = () => {
        const selectedOrders = getSelectedOrders();

        return execReshipOrder({
            modalStore,
            selectedOrders,
            globalStore,
            allOrders,
            updateOrder: orderManagementStore.table.updateOrder,
            reshipOrder: async (canReshipOrders) => {
                await submitReship({ orders: canReshipOrders });
            },
        });
    };

    const handleShipOperation = async ({ key }) => {
        const selectedOrders = getSelectedOrders();

        if (key === ShippingWay.VIRTUAL) {
            await execVirtualShip({
                globalStore,
                selectedOrders,
                shipOrder: submitShip,
            });
        } else if (key === ShippingWay.ONLINE) {
            await execOnlineShip({
                globalStore,
                modalStore,
                allOrders,
                selectedOrders,
                shipOrder: submitShip,
                updateOrder,
            });
        } else if (key === ShippingWay.OFFLINE) {
            await handleOfflineShip();
        }
    };

    const handleJdShipOperation = async ({ key }) => {
        const selectedOrders = getSelectedOrders();

        if (key === "JD_SELLER_DELIVERY") {
            await execJdSellerDelivery({
                selectedOrders,
                onShipTaskDone,
                shipOrder: submitShip,
                globalStore,
            });
        }
    };

    let shipButton = null;

    if (
        tab === OrderTab.PENDING_SHIPMENT ||
        tab === OrderTab.ON_HOLD ||
        tab === OrderTab.PENDING_SHIPMENT_REFUNDING ||
        tab === OrderTab.PRE_SHIP
    ) {
        /** 抖店限制待发货退款中订单发货 */
        if (
            tab === OrderTab.PENDING_SHIPMENT_REFUNDING &&
            DisabledPendingShipmentRefundingShipShopTypes.includes(shop.type)
        ) {
            shipButton = null;
        } else if (shop.type === ShopType.MANUAL || shop.type === ShopType.INDEPENDENT_MANUAL) {
            shipButton = (
                <AutoLoadingButton type="primary" className="ant-btn-warning mr3" onClick={handleOfflineShip}>
                    设为已发货
                </AutoLoadingButton>
            );
        } else if (
            [
                ...SupplierShopTypes,
                ShopType.PINDUODUO,
                ShopType.SUNING,
                ShopType.KWAI_DROPSHIP,
                ShopType.CHANNEL_SHOP,
                ShopType.CHANNEL_SHOP_DROPSHIP,
                ShopType.CHANNEL_SHOP_SUPPLIER,
                ShopType.KTT,
            ].includes(shop.type) ||
            tab === OrderTab.PRE_SHIP
        ) {
            shipButton = (
                <AutoLoadingButton type="primary" className="ant-btn-warning mr3" onClick={handleOfflineShip}>
                    发货
                </AutoLoadingButton>
            );
        } else if (shop.type === ShopType.PDD_FDS) {
            shipButton = (
                <AutoLoadingButton type="primary" className="ant-btn-warning mr3" onClick={handleOfflineShip}>
                    回传运单号
                </AutoLoadingButton>
            );
        } else if ([ShopType.JD].includes(shop.type)) {
            shipButton = (
                <AutoLoadingDropdownButton
                    className="ant-btn-group-warning mr3"
                    onClick={handleOfflineShip}
                    popupRender={() => {
                        return (
                            <Menu onClick={handleJdShipOperation} items={buildMenuItems(<>
                                <MenuItem key="JD_SELLER_DELIVERY">
                                    <span>厂家自送/商家自送</span>
                                </MenuItem>
                            </>)} />
                        );
                    }}
                    trigger={["click"]}
                    placement="topLeft"
                    icon={<CaretUpOutlined />}
                >
                    发货
                </AutoLoadingDropdownButton>
            );
        } else {
            shipButton = (
                <AutoLoadingDropdownButton
                    className="ant-btn-group-warning mr3"
                    onClick={handleOfflineShip}
                    popupRender={() => {
                        return (
                            <Menu onClick={handleShipOperation} items={buildMenuItems(<>
                                <MenuItem key={ShippingWay.VIRTUAL}>
                                    <span>批量发货（无需物流）</span>
                                </MenuItem>

                                {![
                                    ShopType.DOUDIAN,
                                    ShopType.DOUDIAN_DROP_SHIPPING_SUPPLIER,
                                    ShopType.DOUDIAN_IOP,
                                ].includes(shop.type) && (
                                    <MenuItem key={ShippingWay.ONLINE}>
                                        <span>批量发货（在线下单）</span>
                                    </MenuItem>
                                )}

                                <MenuItem key={ShippingWay.OFFLINE}>
                                    <span>批量发货（自己联系物流）</span>
                                </MenuItem>
                            </>)} />
                        );
                    }}
                    trigger={["click"]}
                    placement="topLeft"
                    icon={<CaretUpOutlined />}
                >
                    发货
                </AutoLoadingDropdownButton>
            );
        }
    } else if (tab === OrderTab.SHIPPED) {
        if (shop.type === ShopType.PDD_FDS) {
            shipButton = (
                <Button type="primary" className="ant-btn-warning mr3" onClick={handleReshipOrder}>
                    回传运单号
                </Button>
            );
        }
    }

    return shipButton;
};

export default observer(ShipButton);
